<template>
  <v-container fluid>
    <v-row>
      <!-- name/title card -->
      <v-col>
        <v-card>
          <v-row no-gutters justify="center" align="center">
            <v-col>
              <v-card-title class="headline font-weight-medium">
                Users
              </v-card-title>
              <v-card-subtitle class="title font-weight-regular">{{getOrg}}</v-card-subtitle>
            </v-col>
            <v-col align="right">
              <v-btn
                class="ma-3"
                fab
                dark
                small
                color="blue"
                @click="onAddClick()"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="users"
          :items-per-page="5"
          :loading="loading"
        >
          <template v-slot:item.admin="{ item }">
            <v-icon color="green" v-if="item.admin">mdi-check</v-icon>
          </template>

          <template v-slot:item.userPermissions="{ item }">
            {{ getPermissionsString(item) }}
          </template>

          <template v-slot:item.controls="props">
            <v-row dense>
              <v-col>
                <v-btn
                  icon
                  x-small
                  color="blue"
                  @click="onEditClick(props.item)"
                >
                  <v-icon dark>mdi-pencil</v-icon>
                </v-btn>
              </v-col>
              <!-- admin users cannot be deleted -->
              <v-col>
                <v-btn
                  :disabled="props.item.admin"
                  icon
                  x-small
                  color="red"
                  @click="onDeleteClick(props.item)"
                >
                  <v-icon dark>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <EditUser
      :permissions="getPermissions"
      :user="user"
      :loading="modalLoading"
      v-if="isEditDialogVisible"
      :visible="isEditDialogVisible"
      :error="error"
      v-on:onConfirmed="onEditUserConfirmed"
      v-on:onCancelled="onEditUserCancelled"
    />

    <AddUser
      :permissions="getPermissions"
      :loading="modalLoading"
      :visible="isAddDialogVisible"
      :error="error"
      v-on:onConfirmed="onAddUserConfirmed"
      v-on:onCancelled="onAddUserCancelled"
    />

    <Confirm ref="confirm"></Confirm>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import EditUser from "../../modals/EditUser";
import AddUser from "../../modals/AddUser";
import Confirm from "../../modals/Confirm";

export default {
  name: "Users",
  components: {
    EditUser,
    AddUser,
    Confirm,
  },
  computed: {
    ...mapGetters(["getPermissions", "getOrg"]),
    // TODO Why are getters user for permissions and org but not for users? Is it because this is the users page?
    users() {
      return this.$store.state.users.users;
    },
  },
  data() {
    return {
      user: null,
      isEditDialogVisible: false,
      isAddDialogVisible: false,
      error: "",
      loading: true,
      modalLoading: false,
      headers: [
        { text: "Name", value: "name" },
        { text: "Title", value: "title" },
        { text: "Username", value: "email" },
        { text: "Administrator", value: "admin" },
        { text: "Permissions", value: "userPermissions" },
        {
          text: "Admin",
          value: "controls",
          sortable: false,
          align: "right",
          width: 90,
        },
      ],
    };
  },
  methods: {
    ...mapActions([
      "getUsers",
      "addUser",
      "editUser",
      "deleteUser",
      "setGlobalAlert",
    ]),
    // Open add
    onAddClick: function () {
      this.isAddDialogVisible = true;
    },
    // Open edit
    onEditClick: function (user) {
      this.user = user;
      this.isEditDialogVisible = true;
    },
    // Save edit
    async onEditUserConfirmed(user) {
      this.modalLoading = true;
      this.error = null;
      this.editUser(user)
        .then(() => {
          this.isEditDialogVisible = false;
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertTitle: this.$t("modal.editUser.success"),
          });
        })
        .catch((e) => {
          if (e.response.data.code == 2) {
            this.error = this.$t("error.register.emailExists");
          } else {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertTitle: this.$t("error.general.unhandled"),
            });
          }
        })
        .finally(() => {
          this.modalLoading = false;
        });
    },
    // Cancel edit
    onEditUserCancelled() {
      this.isEditDialogVisible = false;
      this.error = null;
    },
    // Save new
    async onAddUserConfirmed(user) {
      this.modalLoading = true;
      this.error = null;
      this.addUser(user)
        .then(() => {
          this.isAddDialogVisible = false;
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertTitle: this.$t("modal.addUser.success"),
          });
        })
        .catch((e) => {
          if (e.response.data.code == 2) {
            this.error = this.$t("error.register.emailExists");
          } else {
            this.setGlobalAlert({
              alert: true,
              color: "red",
              alertTitle: this.$t("error.general.unhandled"),
            });
          }
        })
        .finally(() => {
          this.modalLoading = false;
        });
    },
    // Cancel new
    onAddUserCancelled() {
      this.isAddDialogVisible = false;
      this.error = null;
    },
    onDeleteClick(user) {
      this.$refs.confirm
        .open(
          "Delete user <b>" + user.email + "</b>?",
          "<p>You are about to delete a user, which cannot be undone.</p> <b>NOTE</b> This will unassign the user from any assigned case groups.",
          { color: "red" }
        )
        .then((confirm) => {
          if (confirm) {
            this.delete(user);
          }
        });
    },
    delete(user) {
      this.loading = true;
      this.deleteUser(user)
        .catch((err) => {
          console.log(err);
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: "There's been a problem",
            alertMessage: "Unable to delete the user. Please try again.",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getPermissionsString(user) {
      if (user.admin) {
        return "*";
      } else {
        return user.permissions == null ||
          user.permissions === undefined ||
          user.permissions.length == 0
          ? ""
          : Array.prototype.map
              .call(user.permissions, function (item) {
                return item.name;
              })
              .join(", ");
      }
    },
  },
  created() {
    /* Action returns a promise inorder to catch errors and show/customize globalAlert */
    this.getUsers()
      .catch(() => {
        this.setGlobalAlert({
          alert: true,
          color: "red",
          alertTitle: "There's been a problem.",
          alertMessage: "Unable to retreive users, support has been notified.",
        });
      })
      .finally(() => {
        this.loading = false;
      });
  },
};
</script>

<style>
/* Fixes unaligned headers */
.v-data-table-header th {
  white-space: nowrap;
}
</style>